// EPSE BESS Simulation Engine
import { 
  BESSConfig, 
  EPSEQuarterData, 
  SimulationResults, 
  SimulationKPIs,
  SOCPoint,
  PeakExceedance,
  DailySummary 
} from '@/types/epse';

/**
 * Run BESS simulation with rule-based dispatch
 */
export function simulateRuleBased(
  quarterData: EPSEQuarterData[],
  bessConfig: BESSConfig,
  connectionLimit: number,
  pvData?: number[]
): { results: SimulationResults; kpis: SimulationKPIs } {
  const {
    capacity_kwh,
    power_kw,
    efficiency_charge,
    efficiency_discharge,
    soc_min,
    soc_max,
    soc_initial,
  } = bessConfig;
  
  let soc = soc_initial * capacity_kwh; // Current SOC in kWh
  const socMin = soc_min * capacity_kwh;
  const socMax = soc_max * capacity_kwh;
  
  const socTrajectory: SOCPoint[] = [];
  const gridImport: number[] = [];
  const gridExport: number[] = [];
  const batteryCharge: number[] = [];
  const batteryDischarge: number[] = [];
  const peakExceedances: PeakExceedance[] = [];
  
  // Track daily summaries
  const dailyMap = new Map<string, {
    import_kwh: number;
    export_kwh: number;
    self_consumed_pv: number;
    curtailed_pv: number;
    peak_kw: number;
    charge_kwh: number;
    discharge_kwh: number;
  }>();
  
  let totalImport = 0;
  let totalExport = 0;
  let totalSelfConsumed = 0;
  let totalCurtailed = 0;
  let totalChargeEnergy = 0;
  let totalDischargeEnergy = 0;
  let peakOverrunsCount = 0;
  let maxPeakBefore = 0;
  let maxPeakAfter = 0;
  
  quarterData.forEach((quarter, i) => {
    const date = new Date(quarter.start_time).toISOString().split('T')[0];
    if (!dailyMap.has(date)) {
      dailyMap.set(date, {
        import_kwh: 0,
        export_kwh: 0,
        self_consumed_pv: 0,
        curtailed_pv: 0,
        peak_kw: 0,
        charge_kwh: 0,
        discharge_kwh: 0,
      });
    }
    const daily = dailyMap.get(date)!;
    
    // Get load and PV
    const load = quarter.kw_value;
    const pv = pvData?.[i] ?? (quarter.pv_kw ?? 0);
    
    // Net load = consumption - PV generation
    let netLoad = load - pv;
    
    // Track original peak
    maxPeakBefore = Math.max(maxPeakBefore, Math.max(0, netLoad));
    
    let battCharge = 0;
    let battDischarge = 0;
    let gridImp = 0;
    let gridExp = 0;
    let selfConsumed = Math.min(pv, load);
    let curtailed = 0;
    
    if (netLoad > connectionLimit) {
      // Discharge battery to reduce peak
      const excess = netLoad - connectionLimit;
      const maxDischarge = Math.min(
        power_kw,
        (soc - socMin) / efficiency_discharge
      );
      battDischarge = Math.min(excess, maxDischarge);
      const actualDischarge = battDischarge * efficiency_discharge;
      soc -= actualDischarge;
      
      netLoad -= battDischarge;
      totalDischargeEnergy += actualDischarge * 0.25;
      daily.discharge_kwh += actualDischarge * 0.25;
      
      if (netLoad > connectionLimit) {
        peakOverrunsCount++;
        peakExceedances.push({
          timestamp: quarter.start_time,
          kw_value: netLoad,
          exceedance_kw: netLoad - connectionLimit,
          duration_minutes: 15,
        });
      }
    } else if (netLoad < connectionLimit && soc < socMax) {
      // Charge battery with available headroom
      const headroom = connectionLimit - netLoad;
      const maxCharge = Math.min(
        power_kw,
        (socMax - soc) * efficiency_charge,
        headroom
      );
      battCharge = maxCharge;
      const actualCharge = battCharge / efficiency_charge;
      soc += battCharge;
      
      netLoad += actualCharge;
      totalChargeEnergy += battCharge * 0.25;
      daily.charge_kwh += battCharge * 0.25;
    }
    
    // Final grid interaction
    if (netLoad > 0) {
      gridImp = netLoad;
      totalImport += gridImp * 0.25;
      daily.import_kwh += gridImp * 0.25;
    } else {
      gridExp = Math.abs(netLoad);
      totalExport += gridExp * 0.25;
      daily.export_kwh += gridExp * 0.25;
      
      // Check for curtailment (export limited by connection)
      if (gridExp > connectionLimit) {
        curtailed = gridExp - connectionLimit;
        gridExp = connectionLimit;
        totalCurtailed += curtailed * 0.25;
        daily.curtailed_pv += curtailed * 0.25;
      }
    }
    
    daily.self_consumed_pv += selfConsumed * 0.25;
    totalSelfConsumed += selfConsumed * 0.25;
    daily.peak_kw = Math.max(daily.peak_kw, Math.max(gridImp, 0));
    
    maxPeakAfter = Math.max(maxPeakAfter, Math.max(gridImp, 0));
    
    // Record results
    socTrajectory.push({
      timestamp: quarter.start_time,
      soc_kwh: soc,
      soc_percent: soc / capacity_kwh,
    });
    gridImport.push(gridImp);
    gridExport.push(gridExp);
    batteryCharge.push(battCharge);
    batteryDischarge.push(battDischarge);
  });
  
  // Build daily summaries
  const dailySummaries: DailySummary[] = Array.from(dailyMap.entries()).map(([date, data]) => ({
    date,
    import_kwh: data.import_kwh,
    export_kwh: data.export_kwh,
    self_consumed_pv_kwh: data.self_consumed_pv,
    curtailed_pv_kwh: data.curtailed_pv,
    peak_kw: data.peak_kw,
    cycles: (data.charge_kwh + data.discharge_kwh) / (2 * capacity_kwh),
  }));
  
  // Calculate total cycles
  const totalCycles = (totalChargeEnergy + totalDischargeEnergy) / (2 * capacity_kwh);
  
  const results: SimulationResults = {
    soc_trajectory: socTrajectory,
    grid_import: gridImport,
    grid_export: gridExport,
    battery_charge: batteryCharge,
    battery_discharge: batteryDischarge,
    peak_exceedances: peakExceedances,
    daily_summaries: dailySummaries,
  };
  
  const kpis: SimulationKPIs = {
    import_kwh: totalImport,
    export_kwh: totalExport,
    self_consumed_pv_kwh: totalSelfConsumed,
    curtailed_pv_kwh: totalCurtailed,
    peak_overruns_count: peakOverrunsCount,
    peak_overruns_duration_hours: peakOverrunsCount * 0.25,
    energy_above_limit_kwh: peakExceedances.reduce((sum, p) => sum + p.exceedance_kw * 0.25, 0),
    bess_cycles: totalCycles,
    bess_throughput_kwh: totalChargeEnergy + totalDischargeEnergy,
    peak_reduction_kw: maxPeakBefore - maxPeakAfter,
    peak_reduction_percent: maxPeakBefore > 0 ? ((maxPeakBefore - maxPeakAfter) / maxPeakBefore) * 100 : 0,
  };
  
  return { results, kpis };
}

/**
 * Run BESS simulation with perfect foresight (theoretical upper bound)
 * This optimizes charge/discharge knowing the entire day's load profile
 */
export function simulatePerfectForesight(
  quarterData: EPSEQuarterData[],
  bessConfig: BESSConfig,
  connectionLimit: number,
  pvData?: number[]
): { results: SimulationResults; kpis: SimulationKPIs } {
  // Group data by day for day-ahead optimization
  const dayGroups = new Map<string, EPSEQuarterData[]>();
  quarterData.forEach(q => {
    const date = new Date(q.start_time).toISOString().split('T')[0];
    if (!dayGroups.has(date)) dayGroups.set(date, []);
    dayGroups.get(date)!.push(q);
  });
  
  const {
    capacity_kwh,
    power_kw,
    efficiency_charge,
    efficiency_discharge,
    soc_min,
    soc_max,
    soc_initial,
  } = bessConfig;
  
  let soc = soc_initial * capacity_kwh;
  const socMin = soc_min * capacity_kwh;
  const socMax = soc_max * capacity_kwh;
  
  const socTrajectory: SOCPoint[] = [];
  const gridImport: number[] = [];
  const gridExport: number[] = [];
  const batteryCharge: number[] = [];
  const batteryDischarge: number[] = [];
  const peakExceedances: PeakExceedance[] = [];
  const dailySummaries: DailySummary[] = [];
  
  let totalImport = 0;
  let totalExport = 0;
  let totalSelfConsumed = 0;
  let totalCurtailed = 0;
  let totalChargeEnergy = 0;
  let totalDischargeEnergy = 0;
  let peakOverrunsCount = 0;
  let maxPeakBefore = 0;
  let maxPeakAfter = 0;
  
  let globalQuarterIndex = 0;
  
  Array.from(dayGroups.entries()).forEach(([date, dayData]) => {
    // For each day, identify peak periods and valley periods
    const netLoads = dayData.map((q, i) => {
      const pv = pvData?.[globalQuarterIndex + i] ?? (q.pv_kw ?? 0);
      return q.kw_value - pv;
    });
    
    // Sort quarters by net load to identify charging opportunities
    const sortedIndices = netLoads
      .map((load, i) => ({ load, i }))
      .sort((a, b) => a.load - b.load);
    
    // Pre-calculate optimal charge times (lowest net load periods)
    const chargeTargets = new Set<number>();
    const dischargeTargets = new Set<number>();
    
    // Identify periods above limit that need discharge
    netLoads.forEach((load, i) => {
      if (load > connectionLimit) dischargeTargets.add(i);
    });
    
    // Identify best charging periods (lowest loads with headroom)
    let chargeNeeded = dischargeTargets.size * power_kw * 0.25 / efficiency_discharge;
    for (const { load, i } of sortedIndices) {
      if (chargeNeeded <= 0) break;
      if (load < connectionLimit - power_kw * 0.5) {
        chargeTargets.add(i);
        chargeNeeded -= power_kw * 0.25 * efficiency_charge;
      }
    }
    
    let dailyImport = 0;
    let dailyExport = 0;
    let dailySelfConsumed = 0;
    let dailyCurtailed = 0;
    let dailyPeak = 0;
    let dailyCharge = 0;
    let dailyDischarge = 0;
    
    dayData.forEach((quarter, localIdx) => {
      const pv = pvData?.[globalQuarterIndex] ?? (quarter.pv_kw ?? 0);
      let netLoad = quarter.kw_value - pv;
      const selfConsumed = Math.min(pv, quarter.kw_value);
      
      maxPeakBefore = Math.max(maxPeakBefore, Math.max(0, netLoad));
      
      let battCharge = 0;
      let battDischarge = 0;
      
      if (dischargeTargets.has(localIdx) && soc > socMin) {
        // Discharge to reduce peak
        const excess = netLoad - connectionLimit;
        const maxDischarge = Math.min(
          power_kw,
          (soc - socMin) / efficiency_discharge,
          excess
        );
        battDischarge = maxDischarge;
        soc -= battDischarge * efficiency_discharge;
        netLoad -= battDischarge;
        totalDischargeEnergy += battDischarge * efficiency_discharge * 0.25;
        dailyDischarge += battDischarge * efficiency_discharge * 0.25;
      } else if (chargeTargets.has(localIdx) && soc < socMax) {
        // Charge during valley
        const headroom = connectionLimit - netLoad;
        const maxCharge = Math.min(
          power_kw,
          (socMax - soc) * efficiency_charge,
          headroom
        );
        battCharge = maxCharge;
        soc += battCharge;
        netLoad += battCharge / efficiency_charge;
        totalChargeEnergy += battCharge * 0.25;
        dailyCharge += battCharge * 0.25;
      }
      
      let gridImp = 0;
      let gridExp = 0;
      let curtailed = 0;
      
      if (netLoad > 0) {
        gridImp = netLoad;
        totalImport += gridImp * 0.25;
        dailyImport += gridImp * 0.25;
        
        if (netLoad > connectionLimit) {
          peakOverrunsCount++;
          peakExceedances.push({
            timestamp: quarter.start_time,
            kw_value: netLoad,
            exceedance_kw: netLoad - connectionLimit,
            duration_minutes: 15,
          });
        }
      } else {
        gridExp = Math.abs(netLoad);
        if (gridExp > connectionLimit) {
          curtailed = gridExp - connectionLimit;
          gridExp = connectionLimit;
          totalCurtailed += curtailed * 0.25;
          dailyCurtailed += curtailed * 0.25;
        }
        totalExport += gridExp * 0.25;
        dailyExport += gridExp * 0.25;
      }
      
      dailySelfConsumed += selfConsumed * 0.25;
      totalSelfConsumed += selfConsumed * 0.25;
      dailyPeak = Math.max(dailyPeak, Math.max(gridImp, 0));
      maxPeakAfter = Math.max(maxPeakAfter, Math.max(gridImp, 0));
      
      socTrajectory.push({
        timestamp: quarter.start_time,
        soc_kwh: soc,
        soc_percent: soc / capacity_kwh,
      });
      gridImport.push(gridImp);
      gridExport.push(gridExp);
      batteryCharge.push(battCharge);
      batteryDischarge.push(battDischarge);
      
      globalQuarterIndex++;
    });
    
    dailySummaries.push({
      date,
      import_kwh: dailyImport,
      export_kwh: dailyExport,
      self_consumed_pv_kwh: dailySelfConsumed,
      curtailed_pv_kwh: dailyCurtailed,
      peak_kw: dailyPeak,
      cycles: (dailyCharge + dailyDischarge) / (2 * capacity_kwh),
    });
  });
  
  const totalCycles = (totalChargeEnergy + totalDischargeEnergy) / (2 * capacity_kwh);
  
  return {
    results: {
      soc_trajectory: socTrajectory,
      grid_import: gridImport,
      grid_export: gridExport,
      battery_charge: batteryCharge,
      battery_discharge: batteryDischarge,
      peak_exceedances: peakExceedances,
      daily_summaries: dailySummaries,
    },
    kpis: {
      import_kwh: totalImport,
      export_kwh: totalExport,
      self_consumed_pv_kwh: totalSelfConsumed,
      curtailed_pv_kwh: totalCurtailed,
      peak_overruns_count: peakOverrunsCount,
      peak_overruns_duration_hours: peakOverrunsCount * 0.25,
      energy_above_limit_kwh: peakExceedances.reduce((sum, p) => sum + p.exceedance_kw * 0.25, 0),
      bess_cycles: totalCycles,
      bess_throughput_kwh: totalChargeEnergy + totalDischargeEnergy,
      peak_reduction_kw: maxPeakBefore - maxPeakAfter,
      peak_reduction_percent: maxPeakBefore > 0 ? ((maxPeakBefore - maxPeakAfter) / maxPeakBefore) * 100 : 0,
    },
  };
}

/**
 * Run simulation with specified dispatch mode
 */
export function runSimulation(
  quarterData: EPSEQuarterData[],
  bessConfig: BESSConfig,
  connectionLimit: number,
  dispatchMode: 'rule_based' | 'perfect_foresight',
  pvData?: number[]
): { results: SimulationResults; kpis: SimulationKPIs } {
  if (dispatchMode === 'perfect_foresight') {
    return simulatePerfectForesight(quarterData, bessConfig, connectionLimit, pvData);
  }
  return simulateRuleBased(quarterData, bessConfig, connectionLimit, pvData);
}

/**
 * Generate default BESS configuration
 */
export function getDefaultBESSConfig(powerKw: number = 100): BESSConfig {
  return {
    capacity_kwh: powerKw * 2, // 2C battery
    power_kw: powerKw,
    efficiency_charge: 0.95,
    efficiency_discharge: 0.95,
    soc_min: 0.1,
    soc_max: 0.9,
    soc_initial: 0.5,
    c_rate_max: 1,
  };
}
